import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigation } from 'react-router'
import { subscribeRouteProgress } from '@/lib/routeProgress'

/** Enough time for the grow to read — not so long it feels laggy. */
const MIN_VISIBLE_MS = 360
/** 0 → ~80% grow when a nav starts. */
const RAMP_MS = 300
/** Zip to 100% when done (the “last millisecond” snap). */
const FINISH_WIDTH_MS = 70
/** Brief hold at full width before fade. */
const COMPLETE_HOLD_MS = 40
/** Fade out after complete — keep it snappy. */
const FADE_MS = 90

/**
 * Top blue loading line — same cue as Lumen/Inertia progress (`#2563eb` / beacon-600).
 * Readable ramp, then a snappy finish to 100% and out.
 */
export function NavigationProgress() {
  const navigation = useNavigation()
  const location = useLocation()
  const [chunkBusy, setChunkBusy] = useState(false)
  const [pathPulse, setPathPulse] = useState(false)

  const [visible, setVisible] = useState(false)
  const [percent, setPercent] = useState(0)
  const [fading, setFading] = useState(false)
  const [finishing, setFinishing] = useState(false)

  const startedAtRef = useRef(0)
  const timersRef = useRef<number[]>([])
  const activeRef = useRef(false)

  const clearTimers = () => {
    for (const id of timersRef.current) window.clearTimeout(id)
    timersRef.current = []
  }

  const schedule = (fn: () => void, ms: number) => {
    const id = window.setTimeout(fn, ms)
    timersRef.current.push(id)
    return id
  }

  useEffect(() => subscribeRouteProgress(setChunkBusy), [])

  // Soft pulse on every location change (fast navigations without Suspense).
  useEffect(() => {
    setPathPulse(true)
    const t = window.setTimeout(() => setPathPulse(false), MIN_VISIBLE_MS)
    return () => window.clearTimeout(t)
  }, [location.key])

  const busy = navigation.state === 'loading' || chunkBusy || pathPulse

  useEffect(() => {
    if (busy) {
      // Still loading: cancel any pending finish so the bar doesn't vanish mid-nav.
      if (activeRef.current && !fading) {
        clearTimers()
        setFinishing(false)
        return
      }

      clearTimers()
      activeRef.current = true
      startedAtRef.current = Date.now()
      setFading(false)
      setFinishing(false)
      setVisible(true)
      setPercent(0)

      // Next frame so the 0% → ramp transition actually animates.
      schedule(() => setPercent(78), 16)
      // Slow trickle if still busy after the main ramp.
      schedule(() => {
        if (activeRef.current) setPercent(88)
      }, RAMP_MS)
      schedule(() => {
        if (activeRef.current) setPercent(92)
      }, RAMP_MS + 350)
      return () => clearTimers()
    }

    if (!activeRef.current) return

    const elapsed = Date.now() - startedAtRef.current
    const wait = Math.max(0, MIN_VISIBLE_MS - elapsed)

    clearTimers()
    schedule(() => {
      setFinishing(true)
      setPercent(100)
      schedule(() => {
        setFading(true)
        schedule(() => {
          setVisible(false)
          setFading(false)
          setFinishing(false)
          setPercent(0)
          activeRef.current = false
        }, FADE_MS)
      }, FINISH_WIDTH_MS + COMPLETE_HOLD_MS)
    }, wait)

    return () => clearTimers()
  }, [busy]) // eslint-disable-line react-hooks/exhaustive-deps -- finishing/fading are local finish state

  useEffect(() => () => clearTimers(), [])

  if (!visible) return null

  const widthTransition = finishing
    ? `width ${FINISH_WIDTH_MS}ms cubic-bezier(0.2, 0.9, 0.3, 1)`
    : percent === 0
      ? 'none'
      : `width ${RAMP_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`

  return (
    <div
      className="pointer-events-none fixed inset-x-0 top-0 z-[9999] h-0.5 overflow-hidden"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(percent)}
      aria-hidden
    >
      <div
        className="h-full bg-beacon-600 shadow-[0_0_8px_rgba(37,99,235,0.55)]"
        style={{
          width: `${percent}%`,
          opacity: fading ? 0 : 1,
          transition: fading
            ? `opacity ${FADE_MS}ms ease-out, width ${FINISH_WIDTH_MS}ms cubic-bezier(0.2, 0.9, 0.3, 1)`
            : widthTransition,
        }}
      />
    </div>
  )
}
